import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { subjectStyle } from "@/lib/constants";
import { Users, Link2, X, Flame, Medal, ClipboardList, BookMarked, PencilRuler, Clock, Eye, Trash2, Target } from "lucide-react";
import { toast } from "sonner";

export default function Parent() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () =>
    api.get("/parent/children").then(({ data }) => setChildren(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const link = async (e) => {
    e.preventDefault();
    setLinking(true);
    try {
      const { data } = await api.post("/parent/link", { code });
      toast.success(`${data.child.name} est maintenant lié à ton compte !`);
      setCode("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLinking(false);
    }
  };

  const unlink = async (child) => {
    try {
      await api.delete(`/parent/unlink/${child.id}`);
      toast.success(`${child.name} a été délié de ton compte`);
      setDetail(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openDetail = async (child) => {
    try {
      const { data } = await api.get(`/parent/children/${child.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const maxMinutes = detail ? Math.max(...detail.week.map((d) => d.minutes), 1) : 1;

  return (
    <div className="space-y-6" data-testid="parent-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Suivi de mes enfants</h1>
        <p className="text-slate-500 text-sm sm:text-base mt-1">Consulte la progression scolaire de tes enfants — en lecture seule.</p>
      </div>

      {/* Lien par code */}
      <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="parent-link-card">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-2">
          <Link2 className="h-5 w-5 text-indigo-600" /> Lier un enfant
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Ton enfant trouve son code dans <span className="font-semibold text-slate-700">Profil → Suivi parent</span> sur son compte ProEdu.
        </p>
        <form onSubmit={link} className="flex gap-3 flex-wrap">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            data-testid="parent-code-input"
            placeholder="Code à 6 caractères (ex : A1B2C3)"
            maxLength={6}
            required
            className="flex-1 min-w-[220px] px-4 py-3 rounded-xl border border-slate-200 text-sm font-mono tracking-[0.3em] uppercase text-center"
          />
          <button
            type="submit"
            disabled={linking}
            data-testid="parent-link-button"
            className="px-6 py-3 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-60"
          >
            {linking ? "Liaison…" : "Lier le compte"}
          </button>
        </form>
      </div>

      {/* Liste des enfants */}
      {loading ? (
        <div className="animate-pulse grid md:grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-3xl" />)}</div>
      ) : children.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-3xl border border-slate-200/80" data-testid="parent-empty">
          <Users className="h-10 w-10 text-indigo-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Aucun enfant lié pour le moment.</p>
          <p className="text-sm text-slate-400 mt-1">Entre le code de ton enfant ci-dessus pour commencer le suivi.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {children.map((c) => (
            <div key={c.id} data-testid={`child-card-${c.id}`} className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm">
              <div className="flex items-center gap-4 mb-5">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center text-white text-lg font-bold font-display">
                  {c.name[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900" data-testid={`child-name-${c.id}`}>{c.name}</p>
                  <p className="text-xs text-slate-500">{c.level} · {c.plan === "premium" ? "Premium" : c.plan === "basique" ? "Basique" : "Gratuit"}</p>
                </div>
                <button onClick={() => openDetail(c)} data-testid={`child-detail-${c.id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100 transition-colors duration-200">
                  <Eye className="h-3.5 w-3.5" /> Détails
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { icon: Medal, value: `Niv. ${c.gamification_level}`, label: `${c.xp} XP`, color: "text-indigo-600" },
                  { icon: Flame, value: `${c.streak} j`, label: "d'affilée", color: "text-orange-500" },
                  { icon: ClipboardList, value: c.homework_pending, label: "devoirs à faire", color: "text-rose-500" },
                  { icon: Clock, value: `${c.week_minutes} min`, label: "cette semaine", color: "text-emerald-600" },
                ].map(({ icon: Icon, value, label, color }, i) => (
                  <div key={i} className="rounded-2xl bg-slate-50 p-3">
                    <Icon className={`h-4 w-4 mx-auto mb-1 ${color}`} />
                    <p className="text-sm font-bold text-slate-900">{value}</p>
                    <p className="text-[10px] text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal détail enfant */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="child-detail-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7 animate-fade-in-up">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center text-white text-xl font-bold font-display">
                  {detail.name[0]?.toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900" data-testid="child-detail-name">{detail.name}</h2>
                  <p className="text-sm text-slate-500">{detail.level} · Niveau {detail.gamification_level} · {detail.xp} XP</p>
                </div>
              </div>
              <button onClick={() => setDetail(null)} data-testid="child-detail-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { icon: ClipboardList, value: `${detail.homework_total - detail.homework_pending}/${detail.homework_total}`, label: "devoirs faits", color: "bg-rose-100 text-rose-600" },
                { icon: PencilRuler, value: detail.exercises_done, label: `exercices (${detail.avg_score}%)`, color: "bg-sky-100 text-sky-600" },
                { icon: BookMarked, value: detail.sheets_count, label: "fiches créées", color: "bg-emerald-100 text-emerald-600" },
                { icon: Clock, value: `${Math.floor(detail.total_minutes / 60)}h${String(detail.total_minutes % 60).padStart(2, "0")}`, label: "temps total", color: "bg-indigo-100 text-indigo-600" },
              ].map(({ icon: Icon, value, label, color }, i) => (
                <div key={i} className="rounded-2xl border border-slate-100 p-4 text-center">
                  <div className={`h-8 w-8 mx-auto rounded-xl ${color} flex items-center justify-center mb-2`}><Icon className="h-4 w-4" /></div>
                  <p className="text-lg font-bold text-slate-900">{value}</p>
                  <p className="text-[10px] text-slate-500">{label}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-slate-100 p-5 mb-6">
              <p className="text-sm font-semibold text-slate-900 mb-3">Temps de travail — 7 derniers jours</p>
              <div className="flex items-end gap-2 h-24">
                {detail.week.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-t-lg bg-indigo-500/80 transition-[height] duration-300" style={{ height: `${Math.max(4, (d.minutes / maxMinutes) * 80)}px` }} title={`${d.minutes} min`} />
                    <span className="text-[10px] text-slate-400">{d.day}</span>
                  </div>
                ))}
              </div>
            </div>

            {detail.goals.length > 0 && (
              <div className="rounded-2xl border border-slate-100 p-5 mb-6">
                <p className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-amber-500" /> Objectifs du mois</p>
                <ul className="space-y-2.5">
                  {detail.goals.map((g, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className={`text-xs flex-1 ${g.done ? "line-through text-slate-400" : "text-slate-700"}`}>{g.title}</span>
                      <span className="text-[11px] font-bold text-slate-500">{g.current}/{g.target}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${g.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {g.done ? "Atteint" : "En cours"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-2xl border border-slate-100 p-5 mb-6">
              <p className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-rose-500" /> Devoirs en cours ({detail.homework_pending_list.length})</p>
              {detail.homework_pending_list.length === 0 ? (
                <p className="text-xs text-slate-400">Aucun devoir en attente — tout est à jour !</p>
              ) : (
                <ul className="space-y-2">
                  {detail.homework_pending_list.map((h) => (
                    <li key={h.id} className="flex items-center gap-2.5 text-xs">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${subjectStyle(h.subject)}`}>{h.subject}</span>
                      <span className="text-slate-700 flex-1 truncate">{h.title}</span>
                      <span className="text-slate-400 shrink-0">{new Date(h.due_date + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button onClick={() => unlink(detail)} data-testid="child-unlink-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-50 text-rose-600 text-sm font-semibold hover:bg-rose-100 transition-colors duration-200">
              <Trash2 className="h-4 w-4" /> Délier cet enfant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
